import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { CatalogController } from "../controllers/catalog";
import { CustomerController } from "../controllers/customer";
import { checkCreditForOrder, customerOnTerms, availableCredit } from "../lib/credit";
import { recallCartMeta, rememberCartMeta, getSavedForLater, saveForLater, removeFromSaved } from "../lib/cartMeta";
import { effectivePrice } from "../lib/pricing";
import QtyStepper from "../components/QtyStepper";
import CartLine from "../components/CartLine";
import OrderSummary from "../components/OrderSummary";
import styles from "./Cart.module.css";

// Two-column shopping cart. Left = line items with edit/save/remove.
// Right = sticky order summary + shipping estimator + promo + tax exempt
// + proceed-to-checkout. Lines are enriched from a session meta cache
// (filled by add-to-cart) plus catalog fallback so reloads still show
// product imagery and pricing context.
export default function Cart() {
  const { user, loading } = useAuth();
  const { items, remove, updateQty } = useCart();
  const { formatAmount } = useCurrency();
  const [enriched, setEnriched] = useState({});
  const [saved, setSaved] = useState(getSavedForLater);
  const [customer, setCustomer] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    CustomerController.get().then((c) => { if (!cancelled) setCustomer(c || null); })
      .catch(() => { if (!cancelled) setCustomer(null); });
    return () => { cancelled = true; };
  }, []);

  // Hydrate display meta for any cart line we don't already know about.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      for (const it of items) {
        const cached = recallCartMeta(it.sku);
        if (cached) { next[it.sku] = cached; continue; }
        // Fallback: best-effort lookup against catalog by parent sku/id.
        const parent = it.sku.split("-")[0];
        try {
          const p = await CatalogController.get(parent.toLowerCase());
          if (p) {
            const colorId = it.sku.split("-")[1]?.toLowerCase();
            const color = p.colors?.find((c) => (c.id || "").toLowerCase() === colorId) || null;
            const size = it.sku.split("-")[2] || null;
            const priced = effectivePrice(p, user, { color, siteMode: "b2b" });
            next[it.sku] = {
              name: p.name,
              image: color?.image || p.image,
              price: priced?.price?.amount,
              color: color ? { name: color.name, hex: color.hex, image: color.image } : null,
              size,
            };
            rememberCartMeta(it.sku, next[it.sku]);
          }
        } catch { /* leave row as bare sku */ }
      }
      if (!cancelled) setEnriched((cur) => ({ ...cur, ...next }));
    })();
    return () => { cancelled = true; };
  }, [items, user]);

  const lines = useMemo(() => items.map((it) => ({
    ...it,
    meta: enriched[it.sku] || recallCartMeta(it.sku) || {},
    unit: enriched[it.sku]?.price ?? it.unitPrice ?? null,
  })), [items, enriched]);

  const subtotal = useMemo(
    () => lines.reduce((n, l) => n + (Number(l.unit) || 0) * (l.qty || 0), 0),
    [lines]
  );
  const totalQty = useMemo(() => lines.reduce((n, l) => n + (l.qty || 0), 0), [lines]);
  const onTerms = customerOnTerms(customer);
  const credit = availableCredit(customer);
  // Only enforce over-limit blocking when paying on terms (the path that
  // would actually consume credit). Card customers don't have a limit
  // gate at the cart level.
  const creditCheck = onTerms || customer?.creditLimit > 0
    ? checkCreditForOrder(customer, subtotal)
    : { ok: true };

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  function onSaveForLater(line) {
    saveForLater({ sku: line.sku, qty: line.qty, meta: line.meta, unit: line.unit });
    setSaved(getSavedForLater());
    remove(line.sku);
  }

  function onMoveBackToCart(line) {
    setSaved(removeFromSaved(line.sku));
    if (line.meta) rememberCartMeta(line.sku, line.meta);
    updateQty(line.sku, line.qty || 1);
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>Shopping Cart</h1>
        <span className={styles.subtitle}>
          {lines.length} Product{lines.length === 1 ? "" : "s"}, {totalQty} Item{totalQty === 1 ? "" : "s"}
        </span>
      </header>

      {lines.length === 0 ? (
        <div className={styles.empty}>
          <h2>Your cart is empty.</h2>
          <p>
            <Link to="/items" className={styles.emptyLink}>Browse the catalog →</Link>
          </p>
        </div>
      ) : (
        <>
        {creditCheck && !creditCheck.ok && (
          <div className={styles.creditAlert} role="alert">
            <strong>Over credit limit by ${creditCheck.overage?.toFixed(2)}.</strong>
            <span>{creditCheck.message} Reduce quantities or contact your account manager.</span>
          </div>
        )}
        {onTerms && credit != null && creditCheck.ok && (
          <div className={styles.creditChip}>
            <span>{customer?.terms} account · available credit:</span>
            <strong>{formatAmount(credit)}</strong>
          </div>
        )}
        <div className={styles.layout}>
          <section className={styles.lines}>
            {lines.map((l) => (
              <CartLine
                key={l.sku}
                line={l}
                onQty={(n) => updateQty(l.sku, n)}
                onRemove={() => remove(l.sku)}
                onSaveForLater={() => onSaveForLater(l)}
                formatAmount={formatAmount}
              />
            ))}

            {saved.length > 0 && (
              <section className={styles.saved}>
                <h2 className={styles.savedTitle}>Saved for Later ({saved.length})</h2>
                {saved.map((s) => (
                  <div key={s.sku} className={styles.savedRow}>
                    <div className={styles.savedThumb}>
                      {s.meta?.image && <img src={s.meta.image} alt="" />}
                    </div>
                    <div className={styles.savedMeta}>
                      <div className={styles.savedName}>{s.meta?.name || s.sku}</div>
                      <div className={styles.savedSub}>
                        {s.meta?.color?.name}
                        {s.meta?.size ? ` · ${s.meta.size}` : ""}
                        {s.unit ? ` · ${formatAmount(s.unit)}` : ""}
                      </div>
                    </div>
                    <div className={styles.savedActions}>
                      <button type="button" className={styles.linkBtn} onClick={() => onMoveBackToCart(s)}>
                        Move to cart
                      </button>
                      <button
                        type="button"
                        className={styles.linkBtn}
                        onClick={() => setSaved(removeFromSaved(s.sku))}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </section>
            )}
          </section>

          <aside className={styles.summary}>
            <OrderSummary
              lines={lines}
              subtotal={subtotal}
              onCheckout={() => nav("/checkout")}
              formatAmount={formatAmount}
              disabled={!creditCheck.ok}
              disabledReason={!creditCheck.ok ? "Order exceeds available credit" : null}
            />
          </aside>
        </div>
        </>
      )}
    </div>
  );
}
